import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Route, Switch, withRouter, Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setActivePage } from './features/ui/uiSlice';
import withProps from './hoc/withProps';
import Welcome from './features/welcome/Welcome';
import RemarketingAuth from './features/welcome/RemarketingAuth';
import NameScreen from './features/family/NameScreen';
import RealtimeLocation from './features/options/RealtimeLocation';
import PlaceAlerts from './features/options/PlaceAlerts';
import CheckIn from './features/options/CheckIn';
import LocationHistory from './features/options/LocationHistory';
import PanicButton from './features/options/PanicButton';
import ChooseTarget from './features/family/ChooseTarget';
import CreatePlace from './features/family/CreatePlace';
import SignupScreen from './features/signup/SignupScreen';
import AddPlace from './features/family/AddPlace';
import CreatingProfile from './features/family/CreatingProfile';
import SuccessScreen from './features/success/SuccessScreen';
import PlanCheckout from './features/PlanCheckout/PlanCheckout';
import RemarketingPaywall from './features/plans/RemarketingPaywall';
import landingTypesList from './helper/landingTypes';
import {
  selectBtToken,
  selectPaymentProvider,
  selectPaymentSettingsStatus,
  selectStripePublishKey,
} from './features/signup/signupSlice';
import usePaymentInit from './helper/usePaymentInit';

const Router = ({ history }) => {
  const dispatch = useDispatch();
  const settingsStatus = useSelector(selectPaymentSettingsStatus);
  const paymentProvider = useSelector(selectPaymentProvider);
  const stripePk = useSelector(selectStripePublishKey);
  const btToken = useSelector(selectBtToken);
  const initProps = useMemo(() => {
    return {
      settingsStatus,
      paymentProvider,
      stripePk,
      btToken,
      withPaypal: process.env.REACT_APP_ENABLE_PAYPAL === 'true',
    };
  }, [settingsStatus, paymentProvider, stripePk, btToken]);
  const payment = usePaymentInit(initProps);

  const routes = [
    {
      path: landingTypesList,
      component: Welcome,
    },
    {
      path: '/fdtrial',
      component: RemarketingAuth,
    },
    {
      path: '/name',
      component: NameScreen,
    },
    {
      path: '/real-time-location',
      component: RealtimeLocation,
    },
    {
      path: '/place-alerts',
      component: PlaceAlerts,
    },
    {
      path: '/check-in',
      component: CheckIn,
    },
    {
      path: '/location-history',
      component: LocationHistory,
    },
    {
      path: '/panic-button',
      component: PanicButton,
    },
    {
      path: '/choose-your-target',
      component: ChooseTarget,
    },
    {
      path: '/create-place',
      component: CreatePlace,
    },
    {
      path: '/add-place',
      component: AddPlace,
    },
    {
      path: '/signup',
      component: SignupScreen,
    },
    {
      path: '/creating-your-profile',
      component: CreatingProfile,
    },
    {
      path: '/remarketing',
      component: withProps(RemarketingPaywall, { ...payment }),
    },
    {
      path: '/checkout',
      component: withProps(PlanCheckout, { ...payment }),
    },
    {
      path: '/success',
      component: SuccessScreen,
    },
  ];

  const {
    location: { pathname },
  } = history;

  const locationHistory = useRef([pathname]);

  const setPath = useCallback(
    (path) => {
      dispatch(setActivePage(path.replace('/', '')));
    },
    [dispatch],
  );

  useEffect(() => setPath(pathname), [pathname, dispatch, setPath]);

  useEffect(() => {
    history?.listen((newLocation, action) => {
      let { current } = locationHistory,
        path = newLocation.pathname,
        prevPath = current[current.length - 1];

      setPath(path);

      if (path !== prevPath) {
        if (action === 'PUSH') {
          if (!current.includes(path)) {
            window.scrollTo(0, 0);
            locationHistory.current.push(path);
            window.sessionStorage.setItem('locationHistory', locationHistory.current);
          } else {
            locationHistory.current = current.slice(0, current.indexOf(path) + 1);
            window.sessionStorage.setItem('locationHistory', locationHistory.current);
          }
        } else {
          path = prevPath;
        }

        history.push({ pathname: path });
      }
    });
  }, [history, setPath]);

  return (
    <Switch>
      {routes.map((route, i) => (
        <Route exact key={i} {...route} />
      ))}
      <Route render={() => <Redirect to="/" />} />
    </Switch>
  );
};

export default withRouter(Router);
